import { useState } from "react";

import { Button } from "../components/Button";
import { requestMagicLink } from "../api";

export function Login() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("sending");
    setError(null);
    try {
      await requestMagicLink(email);
      setStatus("sent");
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "something went wrong");
    }
  }

  return (
    <div className="grid min-h-full place-items-center px-6 py-16">
      <div className="w-full max-w-md">
        {/* Logo above the card */}
        <a href="https://clubbot.ai" className="mb-10 flex items-center justify-center gap-3">
          <div className="w-8 h-8 rounded-sm bg-ink flex items-center justify-center">
            <div className="w-4 h-4 rounded-full bg-accent" />
          </div>
          <span className="font-display font-bold text-xl tracking-tight">ClubBot</span>
        </a>

        <div className="rounded-md border-2 border-ink/15 bg-bg-2 p-8">
          {status === "sent" ? (
            <SentState email={email} />
          ) : (
            <>
              <div className="mb-7">
                <Chip>Sign in</Chip>
                <h1 className="mt-3 font-display text-3xl font-bold leading-tight tracking-tight">
                  Welcome back.
                </h1>
                <p className="mt-3 text-[15px] leading-relaxed text-ink/65">
                  Enter your email — we'll send a one-time sign-in link. No password to remember.
                </p>
              </div>

              <form onSubmit={onSubmit} className="space-y-3">
                <input
                  type="email"
                  required
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full"
                  disabled={status === "sending"}
                  autoFocus
                />
                <Button type="submit" disabled={status === "sending"} className="w-full">
                  {status === "sending" ? "Sending…" : "Email me a sign-in link"}
                </Button>
                {error && (
                  <p className="pt-1 font-mono text-xs tracking-wide text-[#C84B31]">
                    × {error}
                  </p>
                )}
              </form>

              <p className="mt-6 font-mono text-[11px] tracking-wider text-ink/45">
                LINK EXPIRES IN 15 MIN · NO PASSWORDS STORED
              </p>
            </>
          )}
        </div>

        <p className="mt-6 text-center font-mono text-[11px] text-ink/55">
          New here?{" "}
          <a
            href="https://clubbot.ai"
            className="text-ink underline decoration-accent-dim decoration-2 underline-offset-2 hover:decoration-ink"
          >
            See how ClubBot works →
          </a>
        </p>
      </div>
    </div>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-block bg-accent text-ink px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.22em] font-bold">
      {children}
    </span>
  );
}

function SentState({ email }: { email: string }) {
  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <span className="relative inline-flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-60" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
        </span>
        <Chip>Magic link sent</Chip>
      </div>
      <h1 className="font-display text-3xl font-bold leading-tight tracking-tight">
        Check your email.
      </h1>
      <p className="mt-3 text-[15px] leading-relaxed text-ink/65">
        We sent a sign-in link to{" "}
        <span className="font-mono text-sm bg-ink/5 px-1.5 py-0.5">{email}</span>.
        It expires in 15 minutes — click it from any device and you're in.
      </p>
      <p className="mt-6 font-mono text-[11px] tracking-wider text-ink/45">
        CAN'T FIND IT? CHECK SPAM, OR EMAIL HI@CLUBBOT.AI
      </p>
    </div>
  );
}
